import { useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Button from "../components/ui/Button.jsx";
import ReportExportBar from "../components/report/ReportExportBar.jsx";
import { ReportPageFooter, ReportPageHeader } from "../components/report/ReportPageChrome.jsx";
import {
  RemediationSummaryHero,
  RemediationSummaryWorklist,
} from "../components/remediation/RemediationSummaryDocument.jsx";
import { NAVY } from "../components/report/reportNavyTokens.js";
import { useReportSnapshot } from "../hooks/useReportSnapshot.js";
import { buildRemediationSummary } from "../utils/remediationSummary.js";
import { resolveProviderId, getProviderMetaForProject } from "../utils/providerContext.js";
import { resolveReportTenant } from "../utils/reportTenant.js";

const mono = '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace';
const display = '"Space Grotesk", "Inter", system-ui, sans-serif';

export default function RemediationSummaryView() {
  const { projectId, snapshotId } = useParams();
  const navigate = useNavigate();
  const { project, snapshots, snapshot, loading, error } = useReportSnapshot(projectId, snapshotId);
  const providerId = resolveProviderId(project || snapshot);
  const providerMeta = getProviderMetaForProject(project || { providerId });
  const tenant = resolveReportTenant(project, snapshot);
  const activeSnapshotId = snapshot?.snapshotId || snapshotId;
  const summary = useMemo(
    () => (snapshot ? buildRemediationSummary(snapshot, providerId) : null),
    [snapshot, providerId],
  );
  const stickyRef = useRef(null);

  useEffect(() => {
    const stack = stickyRef.current;
    const page = stack?.closest(".remediation-summary-page");
    if (!stack || !page) return;

    const syncOffset = () => {
      page.style.setProperty("--remediation-sticky-offset", `${stack.offsetHeight}px`);
    };
    syncOffset();

    const ro = new ResizeObserver(syncOffset);
    ro.observe(stack);
    window.addEventListener("resize", syncOffset);

    const onScroll = () => {
      stack.classList.toggle("remediation-sticky-stack--scrolled", window.scrollY > 24);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", syncOffset);
      window.removeEventListener("scroll", onScroll);
    };
  }, [summary]);

  const orgName = project?.org?.name || "Project";
  const env = project?.org?.environment || "";

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: NAVY.bg, color: NAVY.fg }}>
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "48px 24px" }}>Loading…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: "100vh", background: NAVY.bg, color: NAVY.fg }}>
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "48px 24px" }}>
          <p style={{ color: NAVY.crit, marginBottom: 16 }}>{error}</p>
          <Button variant="secondary" onClick={() => navigate(`/project/${encodeURIComponent(projectId)}`)}>
            ← Back to workspace
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-theme="dark" className="remediation-summary-page" style={{ minHeight: "100vh", background: NAVY.bg, color: NAVY.fg }}>
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "24px 24px 48px" }}>
        <div
          ref={stickyRef}
          className="no-print remediation-sticky-stack"
          style={{
            position: "sticky",
            top: 0,
            zIndex: 40,
            background: NAVY.bg,
            paddingBottom: 12,
            marginBottom: 8,
            borderBottom: `1px solid ${NAVY.line}`,
          }}
        >
          <ReportPageHeader
            projectId={projectId}
            projectName={orgName}
            snapshotTakenAt={snapshot?.takenAt || snapshot?.takenAtIso}
            variant="navy"
          />
          <div style={{ marginTop: 12 }}>
            <ReportExportBar
              projectId={projectId}
              snapshotId={activeSnapshotId}
              activeDoc="remediation"
              variant="navy"
            />
          </div>
          {summary ? (
            <div style={{ marginTop: 12 }}>
              <RemediationSummaryHero summary={summary} interactive compact />
            </div>
          ) : null}
        </div>

        <header style={{ marginBottom: 20, paddingTop: 8 }}>
          <h1
            style={{
              margin: 0,
              fontSize: 28,
              fontWeight: 700,
              letterSpacing: "-0.02em",
              fontFamily: display,
            }}
          >
            Remediation Summary
          </h1>
          <p style={{ margin: "8px 0 0", fontSize: 13, color: NAVY.mut }}>
            {orgName}
            {env ? ` — ${env}` : ""} · {providerMeta?.displayName || providerId}
          </p>
          {tenant ? (
            <p style={{ margin: "6px 0 0", fontSize: 12, color: NAVY.faint, fontFamily: mono }}>
              Tenant: {tenant}
            </p>
          ) : null}
        </header>

        {summary ? <RemediationSummaryWorklist summary={summary} /> : null}

        <ReportPageFooter
          projectId={projectId}
          projectName={orgName}
          providerLabel={providerMeta?.displayName || providerId}
          snapshots={snapshots}
          activeSnapshotId={activeSnapshotId}
          onSnapshotChange={(sid) =>
            navigate(`/project/${encodeURIComponent(projectId)}/remediation/${encodeURIComponent(sid)}`)
          }
          variant="navy"
        />
      </div>
    </div>
  );
}
